import { corporateSupplierRateTrace } from "../../utils/corporateFeTrace";
import { pickExtendedDealApiBoardBasisRaw } from "./mapCorporateExtendedDealApiRoom";

function parseRoomSetConstraints(roomSet) {
  if (roomSet == null || roomSet === "") return {};
  const token = String(roomSet).trim().toUpperCase();
  const match = token.match(/^A(\d+)(?:C(\d+))?|^P(\d+)$/);
  if (!match) return { roomSet: token };
  if (match[3]) {
    const total = Number(match[3]);
    return { roomSet: token, minAdults: 1, maxAdults: total, maxChildren: 0 };
  }
  const adults = Number(match[1]);
  const kids = Number(match[2] ?? 0);
  return { roomSet: token, minAdults: adults, maxAdults: adults, maxChildren: kids };
}

const SUPPLIER_ROW_MARKERS = [
  "supplier",
  "subType",
  "hotel",
  "roomType",
  "fare",
  "sgl",
  "dbl",
  "trp",
  "chd",
  "brd",
  "nts",
  "cat",
  "rset",
  "roomName",
];

const RATE_FIELD_ALIASES = {
  single: ["sgl", "SGL", "single", "Single", "singleFare", "singlePrice"],
  double: ["dbl", "DBL", "double", "Double", "doubleFare", "doublePrice"],
  triple: ["trp", "TRP", "triple", "Triple", "tripleFare", "triplePrice"],
  child: ["chd", "CHD", "child", "Child", "childFare", "childPrice"],
  fare: ["fare", "FARE", "Fare", "baseFare", "BaseFare"],
};

const INTERNAL_FIELDS = new Set([
  "com",
  "COM",
  "commission",
  "Commission",
  "commissionPercent",
  "commissionPercentage",
  "commissionValue",
  "commission_type",
  "commissionType",
]);

function normalizeText(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase().replace(/\s+/g, " ");
}

function pickField(row, aliases) {
  if (!row || typeof row !== "object") return undefined;
  for (const alias of aliases) {
    if (row[alias] != null && row[alias] !== "") return row[alias];
  }
  const lowerMap = Object.fromEntries(
    Object.entries(row).map(([key, value]) => [String(key).toLowerCase(), value]),
  );
  for (const alias of aliases) {
    const value = lowerMap[String(alias).toLowerCase()];
    if (value != null && value !== "") return value;
  }
  return undefined;
}

function parseSupplierPrice(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    return { amount: Math.round(raw * 100) / 100, suffix: null };
  }
  const text = String(raw).trim();
  if (!text) return null;
  const suffixMatch = text.match(/([A-Za-z])$/);
  const suffix = suffixMatch ? suffixMatch[1].toUpperCase() : null;
  const numeric = Number(text.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return { amount: Math.round(numeric * 100) / 100, suffix };
}

function extractChildFareAmount(childFare, adultsFallback = 0) {
  if (childFare == null) return null;
  if (typeof childFare === "number" && Number.isFinite(childFare) && childFare > 0) {
    return childFare;
  }
  if (typeof childFare === "object" && !Array.isArray(childFare)) {
    const fare = childFare.fare ?? childFare.price ?? childFare.amount;
    const parsed = parseSupplierPrice(fare);
    return parsed?.amount ?? null;
  }
  if (Array.isArray(childFare) && childFare.length) {
    const first = childFare[0];
    const parsed = parseSupplierPrice(first?.fare ?? first?.price ?? first?.amount);
    return parsed?.amount ?? adultsFallback;
  }
  return null;
}

/**
 * Agent-aligned: totalFare on row = room total; adultFare without totalFare = per-person.
 */
export function isTotalRateSource(row, fieldName = null) {
  if (!row || typeof row !== "object") return false;
  const totalFare = parseSupplierPrice(row.totalFare ?? row.TotalFare);
  if (totalFare?.amount != null) return true;
  if (fieldName) {
    const parsed = parseSupplierPrice(pickField(row, [fieldName]));
    if (parsed?.suffix === "T") return true;
    if (parsed?.suffix === "P") return false;
  }
  return false;
}

export function isPerPersonRateSource(row, fieldName = null) {
  if (!row || typeof row !== "object") return false;
  if (isTotalRateSource(row, fieldName)) return false;
  const adultFare = parseSupplierPrice(row.adultFare ?? row.AdultFare);
  if (adultFare?.amount != null) return true;
  if (fieldName) {
    const parsed = parseSupplierPrice(pickField(row, [fieldName]));
    if (parsed?.suffix === "P") return true;
    if (parsed?.suffix === "T") return false;
  }
  return true;
}

function buildRateEntry(row, fieldAliases, occupancyAdults, options = {}) {
  const rawValue = pickField(row, fieldAliases);
  const parsed = parseSupplierPrice(rawValue);
  if (!parsed?.amount) return null;

  const adults = Math.max(1, Number(occupancyAdults) || 1);
  const explicitTotal = options.totalPrice != null ? parseSupplierPrice(options.totalPrice)?.amount : null;

  let totalPrice = explicitTotal;
  let pricePerPerson = parsed.amount;
  let pricingBasis = "per_person";

  if (explicitTotal != null) {
    pricingBasis = "total";
    pricePerPerson = explicitTotal / adults;
  } else if (isTotalRateSource(row, fieldAliases[0])) {
    totalPrice = parsed.amount;
    pricePerPerson = totalPrice / adults;
    pricingBasis = "total";
  } else {
    totalPrice = parsed.amount * adults;
    pricingBasis = parsed.suffix === "T" ? "total" : "per_person";
    if (parsed.suffix === "T") {
      totalPrice = parsed.amount;
      pricePerPerson = totalPrice / adults;
    }
  }

  return {
    pricePerPerson: Math.round(pricePerPerson * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    pricingBasis,
    rawValue,
    field: fieldAliases[0],
  };
}

function extractRoomNameFromRow(row) {
  return (
    pickField(row, [
      "roomTypeName",
      "RoomTypeName",
      "roomName",
      "roomname",
      "roomType",
      "RoomType",
      "boxName",
      "BoxName",
      "name",
      "description",
    ]) ?? null
  );
}

function extractBoardFromRow(row, packageData = null) {
  const apiBoard = pickExtendedDealApiBoardBasisRaw(row, packageData);
  if (apiBoard?.raw) return apiBoard.raw;
  return (
    pickField(row, ["boardBasis", "BoardBasis", "board", "Board", "brd", "BRD", "basis", "mealPlan"]) ?? null
  );
}

function extractSupplierFromRow(row, packageData) {
  return (
    pickField(row, [
      "supplier",
      "Supplier",
      "supplierName",
      "SupplierName",
      "supplierCode",
      "SupplierCode",
      "subType",
      "SubType",
    ]) ??
    packageData?.fullDeal?.supplierDetails?.supplierName ??
    packageData?.fullDeal?.supplierDetails?.supplierId ??
    packageData?.supplierDetails?.supplierName ??
    packageData?.supplierDetails?.supplierId ??
    null
  );
}

function extractNightsFromRow(row, packageData) {
  const nights = pickField(row, ["nts", "NTS", "nights", "Nights", "numNights"]);
  if (nights != null) {
    const parsed = Number(nights);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return packageData?.nights ?? packageData?.fullDeal?.hotel?.nights ?? null;
}

function looksLikeSupplierRateRow(row) {
  if (!row || typeof row !== "object") return false;
  if (row.roomSet || row.RoomSet) return true;

  const keys = Object.keys(row).map((key) => key.toLowerCase());
  const markerHits = SUPPLIER_ROW_MARKERS.filter((marker) =>
    keys.some((key) => key === marker.toLowerCase() || key.includes(marker.toLowerCase())),
  ).length;

  const hasRate =
    pickField(row, RATE_FIELD_ALIASES.single) != null ||
    pickField(row, RATE_FIELD_ALIASES.double) != null ||
    pickField(row, RATE_FIELD_ALIASES.triple) != null ||
    pickField(row, RATE_FIELD_ALIASES.fare) != null ||
    pickField(row, RATE_FIELD_ALIASES.child) != null ||
    row.adultFare != null ||
    row.totalFare != null;

  return markerHits >= 2 && hasRate;
}

function ratesFromMatrixRow(row) {
  const rates = {};
  const single = buildRateEntry(row, RATE_FIELD_ALIASES.single, 1);
  const fare = buildRateEntry(row, RATE_FIELD_ALIASES.fare, 2);
  const dbl = buildRateEntry(row, RATE_FIELD_ALIASES.double, 2);
  const triple = buildRateEntry(row, RATE_FIELD_ALIASES.triple, 3);
  const child = buildRateEntry(row, RATE_FIELD_ALIASES.child, 1, { isChild: true });

  if (single) rates.single = single;
  if (dbl) rates.double = dbl;
  else if (fare) rates.double = fare;
  if (triple) rates.triple = triple;
  if (child) rates.child = child;

  return rates;
}

function ratesFromExtendedRow(row) {
  const roomSet = row.roomSet ?? row.RoomSet ?? row.rset ?? row.RSET;
  const parsedSet = parseRoomSetConstraints(roomSet);
  const adults = parsedSet.maxAdults ?? parsedSet.minAdults ?? null;
  const kids = parsedSet.maxChildren ?? 0;
  if (adults == null) return {};

  const totalFare = parseSupplierPrice(row.totalFare ?? row.TotalFare)?.amount;
  const adultFare = parseSupplierPrice(row.adultFare ?? row.AdultFare)?.amount;
  const childAmount = extractChildFareAmount(row.childFare ?? row.childFareExt ?? row.ChildFare);

  let totalPrice = totalFare;
  if (totalPrice == null && adultFare != null) {
    const childTotal = kids > 0 ? (childAmount ?? adultFare) * kids : 0;
    totalPrice = adultFare * adults + childTotal;
  }
  if (totalPrice == null) return {};

  const entry = {
    pricePerPerson:
      adultFare ??
      Math.round((totalPrice / Math.max(1, adults + kids)) * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    pricingBasis: totalFare != null ? "total" : "per_person",
    rawValue: totalFare ?? adultFare,
    field: totalFare != null ? "totalFare" : "adultFare",
    roomSet: String(roomSet ?? "").toUpperCase(),
    adults,
    kids,
  };

  const rates = {};
  if (adults === 1 && kids === 0) rates.single = entry;
  else if (adults === 2 && kids === 0) rates.double = entry;
  else if (adults === 3 && kids === 0) rates.triple = entry;
  else if (adults === 2 && kids === 1) rates.coupleWithChild = entry;
  else rates.byRoomSet = { [String(roomSet).toUpperCase()]: entry };

  if (childAmount != null && !rates.child) {
    rates.child = {
      pricePerPerson: childAmount,
      totalPrice: childAmount,
      pricingBasis: "per_person",
      rawValue: childAmount,
      field: "childFare",
    };
  }

  return rates;
}

function mergeRates(target, source) {
  if (!source) return target;
  const next = { ...target };
  for (const [key, value] of Object.entries(source)) {
    if (!value) continue;
    if (key === "byRoomSet") {
      next.byRoomSet = { ...(next.byRoomSet ?? {}), ...value };
    } else if (!next[key]) {
      next[key] = value;
    }
  }
  return next;
}

function bundleKey({ roomName, board, supplier }) {
  return `${normalizeText(roomName)}|${normalizeText(board)}|${normalizeText(supplier)}`;
}

function collectSupplierRows(packageData) {
  if (!packageData || typeof packageData !== "object") return [];

  const rawPackage = packageData.rawPackage ?? packageData;
  const fullDeal = packageData.fullDeal ?? rawPackage?.fullDeal ?? rawPackage;
  const snapshot = packageData.selectedPackageOriginalSnapshot ?? null;

  const lists = [
    packageData.extendedDealApiData,
    packageData.extendedDeal,
    rawPackage?.extendedDeal,
    fullDeal?.extendedDeal,
    snapshot?.extendedDeal,
    snapshot?.extended,
    rawPackage?.extended,
    fullDeal?.extended,
    packageData.shortenedDeal,
    rawPackage?.shortenedDeal,
    fullDeal?.shortenedDeal,
    packageData.rooms,
    rawPackage?.rooms,
    fullDeal?.rooms,
    packageData.raw?.rooms,
    packageData.raw,
  ];

  const rows = [];
  const seen = new WeakSet();

  const pushRow = (row, sourcePath) => {
    if (!row || typeof row !== "object" || seen.has(row)) return;
    if (!looksLikeSupplierRateRow(row)) return;
    seen.add(row);
    rows.push({ row, sourcePath });
  };

  for (const list of lists) {
    if (Array.isArray(list)) {
      list.forEach((row, index) => pushRow(row, `list[${index}]`));
    } else if (list && typeof list === "object" && looksLikeSupplierRateRow(list)) {
      pushRow(list, "object");
    }
  }

  const walk = (node, path, depth) => {
    if (!node || typeof node !== "object" || depth > 8) return;
    if (seen.has(node)) return;

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        if (item && typeof item === "object" && looksLikeSupplierRateRow(item)) {
          pushRow(item, `${path}[${index}]`);
        }
      });
      return;
    }

    for (const [key, value] of Object.entries(node)) {
      if (INTERNAL_FIELDS.has(key)) continue;
      const nextPath = path ? `${path}.${key}` : key;
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item && typeof item === "object" && looksLikeSupplierRateRow(item)) {
            pushRow(item, `${nextPath}[${index}]`);
          }
        });
      } else if (value && typeof value === "object" && looksLikeSupplierRateRow(value)) {
        pushRow(value, nextPath);
      } else if (value && typeof value === "object" && depth < 7) {
        walk(value, nextPath, depth + 1);
      }
    }
  };

  walk(packageData, "packageData", 0);
  return rows;
}

function buildBundleFromRow(row, sourcePath, packageData) {
  const roomName = extractRoomNameFromRow(row);
  if (!roomName) return null;

  const board = extractBoardFromRow(row, packageData);
  const supplier = extractSupplierFromRow(row, packageData);
  const nights = extractNightsFromRow(row, packageData);

  const hasMatrixRates =
    pickField(row, RATE_FIELD_ALIASES.single) != null ||
    pickField(row, RATE_FIELD_ALIASES.double) != null ||
    pickField(row, RATE_FIELD_ALIASES.triple) != null ||
    pickField(row, RATE_FIELD_ALIASES.fare) != null;

  const rates = hasMatrixRates ? ratesFromMatrixRow(row) : ratesFromExtendedRow(row);
  if (!Object.keys(rates).length) return null;

  return {
    roomName: String(roomName).trim(),
    board: board != null ? String(board).trim() : null,
    supplier: supplier != null ? String(supplier).trim() : null,
    nights,
    rates,
    raw: row,
    sourcePath,
    key: bundleKey({ roomName, board, supplier }),
  };
}

/**
 * Extract supplier occupancy rate bundles from packageData/raw.
 * Maps SGL/DBL/TRP/CHD matrix rows and agent extendedDeal roomSet rows.
 */
export function extractSupplierOccupancyRates(packageData) {
  const rowEntries = collectSupplierRows(packageData);
  const bundlesByKey = new Map();

  for (const { row, sourcePath } of rowEntries) {
    const partial = buildBundleFromRow(row, sourcePath, packageData);
    if (!partial) continue;

    const existing = bundlesByKey.get(partial.key);
    if (existing) {
      existing.rates = mergeRates(existing.rates, partial.rates);
      existing.rawRows = [...(existing.rawRows ?? [existing.raw]), partial.raw];
    } else {
      bundlesByKey.set(partial.key, { ...partial, rawRows: [partial.raw] });
    }
  }

  const bundles = [...bundlesByKey.values()];

  corporateSupplierRateTrace("rates extracted", {
    bundleCount: bundles.length,
    rowCount: rowEntries.length,
    bundles: bundles.map((bundle) => ({
      roomName: bundle.roomName,
      supplier: bundle.supplier,
      board: bundle.board,
      hasSingle: Boolean(bundle.rates.single),
      hasDouble: Boolean(bundle.rates.double),
      hasTriple: Boolean(bundle.rates.triple),
      hasChild: Boolean(bundle.rates.child),
      singlePrice: bundle.rates.single?.totalPrice ?? null,
      doublePrice: bundle.rates.double?.totalPrice ?? null,
      triplePrice: bundle.rates.triple?.totalPrice ?? null,
      childPrice: bundle.rates.child?.totalPrice ?? null,
    })),
  });

  return bundles;
}

export function findSupplierOccupancyRatesForRoomOption(roomOption, rateBundles = []) {
  if (!roomOption || !rateBundles.length) return null;

  const roomName = roomOption.name ?? extractRoomNameFromRow(roomOption.raw ?? roomOption);
  const board = roomOption.board ?? roomOption.boardCode ?? extractBoardFromRow(roomOption.raw ?? roomOption);
  const supplier =
    roomOption.supplier ??
    roomOption.supplierLabel ??
    extractSupplierFromRow(roomOption.raw ?? roomOption, {});

  const key = bundleKey({ roomName, board, supplier });
  let match = rateBundles.find((bundle) => bundle.key === key);

  if (!match && roomName) {
    const normalizedName = normalizeText(roomName);
    match = rateBundles.find(
      (bundle) =>
        normalizeText(bundle.roomName) === normalizedName &&
        (!board || !bundle.board || normalizeText(bundle.board) === normalizeText(board)),
    );
  }

  return match ?? null;
}

export function buildPriceByOccupancyFromSupplierRates(rates) {
  if (!rates || typeof rates !== "object") return {};

  const out = {};
  if (rates.single?.totalPrice) out["1A0K0I"] = rates.single.totalPrice;
  if (rates.double?.totalPrice) out["2A0K0I"] = rates.double.totalPrice;
  if (rates.triple?.totalPrice) out["3A0K0I"] = rates.triple.totalPrice;
  if (rates.coupleWithChild?.totalPrice) out["2A1K0I"] = rates.coupleWithChild.totalPrice;
  else if (rates.double?.totalPrice && rates.child?.totalPrice) {
    out["2A1K0I"] = Math.round((rates.double.totalPrice + rates.child.totalPrice) * 100) / 100;
  }
  return out;
}

function createRoomOptionFromRateBundle(bundle, templateOption = null) {
  const priceByOccupancy = buildPriceByOccupancyFromSupplierRates(bundle.rates);
  const doublePrice = bundle.rates.double?.totalPrice ?? bundle.rates.coupleWithChild?.totalPrice ?? null;

  return {
    ...(templateOption ?? {}),
    id: templateOption?.id ?? `${bundle.key}-${bundle.sourcePath ?? "supplier"}`,
    name: bundle.roomName,
    board: templateOption?.board ?? bundle.board,
    boardCode: templateOption?.boardCode ?? bundle.board,
    supplier: bundle.supplier,
    supplierLabel: bundle.supplier,
    supplierOccupancyRates: bundle.rates,
    priceByOccupancy,
    price: templateOption?.price ?? doublePrice,
    priceNis: templateOption?.priceNis ?? doublePrice,
    totalPrice: templateOption?.totalPrice ?? doublePrice,
    raw: {
      ...(templateOption?.raw ?? bundle.raw ?? {}),
      priceByOccupancy,
      supplierOccupancyRates: bundle.rates,
    },
    sourceRoom: templateOption?.sourceRoom ?? bundle.raw,
    sourcePath: templateOption?.sourcePath ?? bundle.sourcePath,
  };
}

export function mergeRoomOptionsWithSupplierRates(roomOptions, rateBundles, packageData, searchState = {}) {
  if (!Array.isArray(roomOptions) || !roomOptions.length) {
    return roomOptions ?? [];
  }

  const fallbacks = roomOptions.filter((option) => option?.isFallback);
  const actionable = roomOptions.filter((option) => option && !option.isFallback);

  if (!rateBundles.length) {
    return roomOptions;
  }

  const enriched = actionable.map((option) => {
    const bundle = findSupplierOccupancyRatesForRoomOption(option, rateBundles);
    if (!bundle) return option;
    return createRoomOptionFromRateBundle(bundle, option);
  });

  const representedRoomNames = new Set(
    actionable.map((option) => normalizeText(option.name)).filter(Boolean),
  );
  const representedKeys = new Set(
    enriched
      .map((option) => findSupplierOccupancyRatesForRoomOption(option, rateBundles)?.key)
      .filter(Boolean),
  );

  const supplierVariants = [];
  for (const bundle of rateBundles) {
    if (!representedRoomNames.has(normalizeText(bundle.roomName))) continue;
    if (representedKeys.has(bundle.key)) continue;
    supplierVariants.push(createRoomOptionFromRateBundle(bundle));
    representedKeys.add(bundle.key);
  }

  const merged = [...enriched, ...supplierVariants, ...fallbacks];
  if (!merged.length) return roomOptions;

  corporateSupplierRateTrace("room options merged with supplier rates", {
    beforeCount: roomOptions.length,
    afterCount: merged.length,
    options: merged
      .filter((option) => !option?.isFallback)
      .map((option) => ({
        roomName: option.name,
        supplier: option.supplierLabel,
        priceByOccupancy: option.priceByOccupancy,
      })),
  });

  return merged;
}
